import { useState, useEffect, useMemo } from 'react';

/**
 * Custom hook to track if an element is visible on screen using Intersection Observer API.
 *
 * @param {object} ref - A React ref object pointing to the DOM element being observed.
 * @returns {boolean} - A boolean indicating if the element is intersecting with the viewport.
 *
 * The hook uses `IntersectionObserver` to track element visibility. Returns `true` when the
 * element is visible, and `false` otherwise. Observer is memoized to avoid re-creation when
 * `ref` doesn't change. Effect attaches and detaches the observer based on the element's state.
 */
export default function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(() => new IntersectionObserver(
    ([entry]) => setIntersecting(entry.isIntersecting),
  ), [ref]);

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [observer, ref]);

  return isIntersecting;
}
